// External Imports
import React, { useEffect, useState, useContext } from 'react';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { SubmissionError, submit, change } from 'redux-form';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

// PPUI Imports
import {
  Alert,
  Container,
  Col,
  Row,
  Button,
  ArrowLeftIcon,
  IconButton,
} from '@paypalcorp/pp-react';
import { ProgressMeter } from '@paypalcorp/pp-react-progress-meter';

// Internal Imports
import WizardStep1Form from './WizardStepsV3/WizardStep1';
import WizardStep2Form from './WizardStepsV3/WizardStep2';
import WizardStep3Form from './WizardStepsV3/WizardStep3';
import WizardStep4Form from './WizardStepsV3/WizardStep4';
import ExtendSession from '../shared/alerts/ExtendSessionModal';
import { getUserType, isLoggedInUser } from '../utilsV3/authUtils';
import { hideSpinner, showSpinner } from '../utilsV3/spinnerUtils';
import ButtonFactoryLogin from '../shared/login/ButtonFactoryLogin';
import PreviewNavigator from '../shared/PreviewNavigator';
import * as ButtonFactoryActions from '../actions/actionsButtonFactory';
import { getParameterByName } from '../../utility/queryStringHelper';
import {
  getPageUrl,
  getStepUrl,
  isDonateButton,
  isHostedFlow,
  isUnhostedFlow,
  getWizardTotalSteps,
  getFlowType,
  isDonateLink,
  handleLoginHelper,
  handleSignupHelper,
  isCsamHit,
  scrollToWizardsTop,
  getOptionsForLanguageField,
} from '../utilsV3/buttonFactoryUtils';
import { trackWizardDetailsPage } from '../utilsV3/ButtonFactoryFPT';
import {
  mapServerFieldToClientField,
  shouldUsePayPalButtonStyle,
} from '../lib/buttonModelV3';
import DonateButtonFeedback from '../shared/DonateButtonFeedback';
import WizardMobilePreview from './preview/WizardMobilePreview';
import { BTN_FACTORY_V3_FORM, ERRORS } from '../clientConstants';
import WizardStep1Preview from './preview/WizardStep1Preview';
import { FPTI_SPEC, SCREEN, ACTION } from '../utilsV3/fptiConstants';
import {
  trackEventWithData,
  trackEventWithGlobalData,
} from '../utilsV3/analytics';
import {
  LOGO_TARGET_BUTTON,
  LOGO_TARGET_PROFILE,
  LOGO_TARGET_NOLOGO,
} from '../reducers/reducerButtonFactoryV3';
import { extendSessionAPI } from '../shared/extendSession/extendSessionUtils';
import { GenericCreationErrorModal } from '../shared/alerts/GenericCreationErrorModal';
import { ServerValidationErrorModal } from '../shared/alerts/ServerValidationErrorModal';
import { GenericDataLoadFailureModal } from '../shared/alerts/GenericDataLoadFailureModal';
import { QuitModal } from '../shared/alerts/QuitModal';
import { DuplicateToast } from '../shared/alerts/Toasts';
import withTheme from './withTheme';
import { Context as WorldReadyContext } from '@paypalcorp/worldready-react';
import { MessageFormat } from '@paypalcorp/worldready';
import { recordConsent } from '../utilsV3/recordConsent';
import { getFormattedTimeOnlyFromUnix } from '../utilsV3/dateUtils';
import { getDonateLocaleAPIFormat } from '../utilsV3/localeUtils';
import { deleteButton } from './buttonManager/actionsManager';
import { useIsMobile } from '../../utility/isMobile';
import { DraftAbandonmentErrorModal } from '../shared/alerts/DraftAbandonmentErrorModal';
import { useHasScrolledToPosition } from '@paypalcorp/donate-react';

const ButtonFactoryWizardV3 = ({
  application = {},
  buttonFactoryV3 = {},
  destroyForm,
  dispatch,
  form: { buttonFactoryV3: { values: formValues = {} } = {} } = {},
  getButton,
  goToNextStep,
  goToPreviousStep,
  history,
  initializeWizard,
  location,
  match,
  saveButton,
  uploadImage,
  updateBusinessProfileLogo,
  updateBusinessImageUrls,
  updateFlowSpec,
  setCurrentStep,
  openMobilePreview,
  closeMobilePreview,
  optionsForLanguage,
  draftBusy,
  uploadImageFail,
  isDraftButton,
}) => {
  const worldReady = useContext(WorldReadyContext);
  const { busy, currentStep, totalSteps } = buttonFactoryV3;
  const [cancelBtnFptiSpec, setCancelBtnFptiSpec] = useState({});
  const [backBtnFptiSpec, setBackBtnFptiSpec] = useState({});
  const [continueBtnFptiSpec, setContinueBtnFptiSpec] = useState({});
  const [saveBtnFptiSpec, setSaveBtnFptiSpec] = useState({});
  const [finishBtnFptiSpec, setFinishBtnFptiSpec] = useState({});
  const [backToManageFptiSpec, setBackToManageFptiSpec] = useState({});
  const [feedbackBtnFptiSpec, setFeedbackBtnFptiSpec] = useState({});
  const [previewTabFptiSpec, setPreviewTabFptiSpec] = useState({});
  const [screen, setScreen] = useState('');
  const [showDraftError, setShowDraftError] = useState(false);
  const isMobile = useIsMobile();

  // Wizard step 1 Dropzone handle variables
  const [showButtonSpinner, setShowButtonSpinner] = useState(false);
  const [imageUpdated, setImageUpdated] = useState(false);
  const [dropzoneError, setDropzoneError] = useState('');

  const [logoClientError, setLogoClientError] = useState('');
  const [bannerClientError, setBannerClientError] = useState('');

  const progressMeterTitles = isDonateButton()
    ? {
        1: new MessageFormat(worldReady, {
          id: 'buttonsV3.progressMeter.stepTitle.button.1',
        }).format(),
        2: new MessageFormat(worldReady, {
          id: 'buttonsV3.progressMeter.stepTitle.button.2',
        }).format(),
        3: new MessageFormat(worldReady, {
          id: 'buttonsV3.progressMeter.stepTitle.button.3',
        }).format(),
        4: new MessageFormat(worldReady, {
          id: 'buttonsV3.progressMeter.stepTitle.button.4',
        }).format(),
      }
    : {
        1: new MessageFormat(worldReady, {
          id: 'buttonsV3.progressMeter.stepTitle.link.1',
        }).format(),
        2: new MessageFormat(worldReady, {
          id: 'buttonsV3.progressMeter.stepTitle.link.2',
        }).format(),
        3: new MessageFormat(worldReady, {
          id: 'buttonsV3.progressMeter.stepTitle.link.3',
        }).format(),
      };

  // Workaround for issue in content fro link flow step 2
  // TODO: Remove this if once we get correct content fro progressMeter.properties
  if (isDonateLink()) {
    progressMeterTitles[2] = new MessageFormat(worldReady, {
      id: 'buttonsV3.progressMeter.stepTitle.button.3',
    }).format();
  }

  const isHosted = isHostedFlow();
  const isButtonFlow = isDonateButton();
  const isButtonConfigurePage = isButtonFlow && currentStep === 1,
    isExperienceSetupPage =
      (isButtonFlow && currentStep === 2) ||
      (!isButtonFlow && currentStep === 1),
    isSetAmountPage =
      (isButtonFlow && currentStep === 3) ||
      (!isButtonFlow && currentStep === 2),
    isAdvancedSettingsPage =
      (isButtonFlow && isHosted && currentStep === 4) ||
      (!isButtonFlow && currentStep === 3);

  const {
    amountType,
    bannerImageUrl,
    buttonImageUrl,
    buttonSize,
    cancelReturn,
    countryCode,
    coverFeesEnabled,
    extraVariablesView,
    language,
    listPrograms,
    noAdvancedVariables,
    noBannerImage,
    noCancelReturn,
    noLogoImage,
    noNote,
    noShipping,
    purpose,
    receiverCurrency,
    recurringEnabled,
    return: returnUrl,
    showAcceptedCardLogos,
    useFullPageExperience,
    usePaypalButtonStyle,
    frequencies,
  } = formValues;
  const isDuplicating = !(match.path.indexOf('/duplicate') === -1);
  const isEditing = !(match.path.indexOf('/edit') === -1);

  const [showQuitModal, setShowQuitModal] = useState(false);
  const [shouldShowActionableErrorModal, setShouldShowActionableErrorModal] =
    useState(false);
  const [shouldShowGetConsentError, setShouldShowGetConsentError] =
    useState(false);
  const [errorCode, setErrorCode] = useState(null);
  const [errorDesc, setErrorDesc] = useState(null);
  const [
    shouldShowUnactionableErrorModal,
    setShouldShowUnactionableErrorModal,
  ] = useState(false);
  const [shouldShowDuplicateToast, setShouldShowDuplicateToast] =
    useState(false);
  const [draftInfo, setDraftInfo] = useState('');
  const [showDraftCreationErrorModal, setShowDraftCreationErrorModal] =
    useState(false);

  const { id, status, buttonName } = useSelector((state) => {
    return state?.buttonFactoryV3?.formattedButton ?? {};
  });

  const formSubmitStatus = useSelector(
    (state) => state?.form?.buttonFactoryV3?.submitFailed
  );

  const stickToTop = useHasScrolledToPosition(150);

  const isDraft = (status === 'D' || !id) && getUserType() !== 'PERSONAL';
  const buttonStatus = isDraft ? 'DRAFT' : 'ACTIVE';

  const [handleQuitForm, setHandleQuitForm] = useState(null);

  const initializeWizardComponent = (button) => {
    initializeWizard(button);

    if (currentStep !== 1) {
      const firstPageUrl = getStepUrl(location, currentStep, 1);
      history.replace(firstPageUrl);
    }
  };

  const handleUnhostedButton = () => {
    const { button = {} } = buttonFactoryV3;
    if (_isEmpty(button)) {
      window.location.href = `/donate/buttons${location.search}`;
    } else {
      initializeWizardComponent(button);
    }
  };

  const handleButtonPreviewError = () => {};

  const getLanguage = () => {
    if (language) {
      return language;
    }
    const languages = optionsForLanguage;
    // Return first language
    return _get(languages, '[0].value');
  };

  const setFptiSpecs = () => {
    if (isButtonConfigurePage) {
      setContinueBtnFptiSpec(FPTI_SPEC.CONFIGURE_BUTTON_PAGE_CONTINUE_CL);
      setBackToManageFptiSpec(FPTI_SPEC.CONFIGURE_DONATION_PAGE_NAV_CL);
      setFeedbackBtnFptiSpec(FPTI_SPEC.CONFIGURE_DONATION_PAGE_FEEDBACK_CL);
      setSaveBtnFptiSpec(FPTI_SPEC.CONFIGURE_DONATION_PAGE_SAVE_CL);
      setScreen(SCREEN.CONFIGURE_BUTTON);
    } else if (isExperienceSetupPage) {
      setContinueBtnFptiSpec(FPTI_SPEC.EXPERIENCE_SETUP_PAGE_CONTINUE_CL);
      setBackBtnFptiSpec(FPTI_SPEC.EXPERIENCE_SETUP_PAGE_BACK_CL);
      setBackToManageFptiSpec(FPTI_SPEC.EXPERIENCE_SETUP_PAGE_NAV_BACK_CL);
      setFeedbackBtnFptiSpec(FPTI_SPEC.EXPERIENCE_SETUP_PAGE_FEEDBACK_CL);
      setPreviewTabFptiSpec(FPTI_SPEC.EXPERIENCE_SETUP_PAGE_PREVIEW_TAB_CL);
      setSaveBtnFptiSpec(FPTI_SPEC.EXPERIENCE_SETUP_PAGE_SAVE_CL);
      setScreen(SCREEN.EXPERIENCE_SETUP);
    } else if (isSetAmountPage) {
      setContinueBtnFptiSpec(FPTI_SPEC.SET_AMOUNT_PAGE_CONTINUE_CL);
      setBackBtnFptiSpec(FPTI_SPEC.SET_AMOUNT_PAGE_BACK_CL);
      setBackToManageFptiSpec(FPTI_SPEC.SET_AMOUNT_PAGE_NAV_BACK_CL);
      setFeedbackBtnFptiSpec(FPTI_SPEC.SET_AMOUNT_PAGE_FEEDBACK_CL);
      setPreviewTabFptiSpec(FPTI_SPEC.SET_AMOUNT_PAGE_PREVIEW_TAB_CL);
      setSaveBtnFptiSpec(FPTI_SPEC.SET_AMOUNT_PAGE_SAVE_CL);
      setScreen(SCREEN.SET_AMOUNT);
    } else if (isAdvancedSettingsPage) {
      setBackBtnFptiSpec(FPTI_SPEC.ADVANCED_SETTINGS_PAGE_BACK_CL);
      setBackToManageFptiSpec(FPTI_SPEC.ADVANCED_SETTINGS_PAGE_NAV_BACK_CL);
      setFeedbackBtnFptiSpec(FPTI_SPEC.ADVANCED_SETTINGS_PAGE_FEEDBACK_CL);
      setPreviewTabFptiSpec(FPTI_SPEC.ADVANCED_SETTINGS_PAGE_PREVIEW_TAB_CL);
      setSaveBtnFptiSpec(FPTI_SPEC.ADVANCED_SETTINGS_PAGE_SAVE_CL);
      setScreen(SCREEN.ADVANCED_SETTINGS);
    }

    if (isButtonConfigurePage) {
      setCancelBtnFptiSpec(FPTI_SPEC.CONFIGURE_BUTTON_PAGE_CANCEL_CL);
    } else if (isExperienceSetupPage && !isButtonFlow) {
      setCancelBtnFptiSpec(FPTI_SPEC.EXPERIENCE_SETUP_PAGE_CANCEL_CL);
    }

    if (isAdvancedSettingsPage) {
      setFinishBtnFptiSpec(FPTI_SPEC.ADVANCED_SETTINGS_PAGE_FINISH_CL);
    } else if (isSetAmountPage && isUnhostedFlow) {
      setFinishBtnFptiSpec(FPTI_SPEC.SET_AMOUNT_PAGE_FINISH_CL);
    }
  };

  useEffect(() => {
    if (!application.userConsentData) {
      setShouldShowGetConsentError(true);
      setErrorCode('GET_CONSENT_FAILED');
      setErrorDesc('Failed to fetch user consent status');
      hideSpinner();
    }
  }, []);

  useEffect(() => {
    const buttonId = _get(match, 'params.buttonId');
    const isLoggedIn = isLoggedInUser(application);

    if (!isLoggedIn) {
      hideSpinner();
      return;
    }

    dispatch(isDraftButton(isDraft));

    if (buttonId === 'unhosted') {
      handleUnhostedButton();
      return;
    }

    if (!_isEmpty(buttonId)) {
      showSpinner();
      getButton(buttonId)
        .then((response) => {
          if (!_isEmpty(response?.name)) {
            const code = response?.name;
            window.location.href = `/donate/buttons/manage?error=${code}`;
          } else {
            const flowType = getFlowType();
            updateFlowSpec({
              flowType,
              totalSteps: getWizardTotalSteps(flowType),
            });
            hideSpinner();
          }
        })
        .catch((error) => {
          showSpinner();
          const code = _get(error, 'responseJSON.button.name');
          window.location.href = `/donate/buttons/manage?error=${code}`;
        });
    } else {
      // If buttonId is not present, meaning that this is new button
      // creation and the edit/duplicate routes are excluded
      // We need to check if fromManage=true is available in the query
      // param, if not, redirect user to manage page
      const fromManage = getParameterByName('fromManage');
      if (!fromManage) {
        window.location.href = '/donate/buttons/manage';
      } else {
        initializeWizardComponent();
        const flowType = getFlowType();
        updateFlowSpec({
          flowType,
          totalSteps: getWizardTotalSteps(flowType),
        });
        hideSpinner();
      }
    }

    setCurrentStep(currentStep);

    if (isDuplicating) {
      setShouldShowDuplicateToast(true);
    }
  }, []);

  useEffect(() => {
    setFptiSpecs();
  }, [currentStep]);

  const handleLogin = () => {
    trackEventWithData(FPTI_SPEC.LANDING_PAGE_LOGIN_CL, { version: '3.0' });
    handleLoginHelper(history);
  };

  const handleSignUp = () => {
    trackEventWithData(FPTI_SPEC.LANDING_PAGE_SIGNUP_CL, { version: '3.0' });
    handleSignupHelper(history);
  };

  const handleBackClick = () => {
    extendSessionAPI({ currentStep: currentStep, triggerEvent: false });
    const prevPageUrl = getStepUrl(location, currentStep, currentStep - 1);

    trackEventWithData(backBtnFptiSpec, { step: currentStep });

    goToPreviousStep();
    history.push(prevPageUrl);
  };

  const handleContinueClick = (event) => {
    if (!isDraft) {
      extendSessionAPI({ currentStep: currentStep, triggerEvent: false });
    }
    const nextPageUrl = getStepUrl(location, currentStep, currentStep + 1);

    let enabledFrequencies = [];
    if (recurringEnabled === 'yes') {
      enabledFrequencies = isHostedFlow() ? frequencies.join(',') : 'Monthly';
    }

    trackEventWithGlobalData(continueBtnFptiSpec, {
      ...(isButtonConfigurePage
        ? {
            button_size: buttonSize,
            button_type:
              usePaypalButtonStyle === 'yes' ? 'PAYPAL' : 'customized',
            ...(usePaypalButtonStyle === 'yes'
              ? { card_logo: showAcceptedCardLogos ? 'Yes' : 'No' }
              : {}),
            page_language: language,
            receiver_country: countryCode,
            step: currentStep,
          }
        : {}),
      ...(isExperienceSetupPage
        ? {
            display_type:
              useFullPageExperience === 'no' ? 'pop-up' : 'full page',
            logo: noLogoImage === 'yes' ? 'off' : 'on',
            img: noBannerImage === 'yes' ? 'off' : 'on',
            message: !_isEmpty(purpose) ? 'yes' : 'no',
            step: currentStep,
          }
        : {}),
      ...(isSetAmountPage
        ? {
            amount_type: amountType,
            choose_program: listPrograms === 'yes' ? 'on' : 'off',
            curr: receiverCurrency,
            fee_type: coverFeesEnabled === 'yes' ? 'offset' : 'none',
            is_recurring: recurringEnabled === 'yes' ? 'on' : 'off',
            step: currentStep,
            enabled_frequencies: enabledFrequencies,
          }
        : {}),
      status: buttonStatus,
    });

    if (isDraft) {
      handleSaveDraft(event, true);
      return;
    }

    goToNextStep();
    history.push(nextPageUrl);
    scrollToWizardsTop(isMobile);
  };

  const handleCancelClick = () => {
    if (!isDraft) {
      extendSessionAPI({ currentStep: currentStep });
    }
    trackEventWithData(cancelBtnFptiSpec, { step: currentStep });
    setShowQuitModal(true);
  };

  const handleTopCancelClick = () => {
    if (!isDraft) {
      extendSessionAPI({ currentStep: currentStep });
    }
    trackEventWithData(backToManageFptiSpec, {
      step: currentStep,
      status: buttonStatus,
      action: ACTION.QUIT,
    });
    setShowQuitModal(true);
  };

  const getErrorMsg = (field) => {
    const fieldLbl = new MessageFormat(worldReady, {
      id: `buttonsV3.errors.actionable.${field}`,
    }).format();
    if (fieldLbl !== `buttonsV3.errors.actionable.${field}`) {
      return fieldLbl;
    }
    return new MessageFormat(worldReady, {
      id: 'buttonsV3.errors.actionable.default',
    }).format();
  };

  const jumpToErrorPage = (field) => {
    const currentPath = location.pathname;
    let targetPage = 1;

    if (['buttonImageUrl', 'countryCode', 'language'].includes(field)) {
      targetPage = 1;
    } else if (['purpose', 'logoUrl', 'bannerImageUrl'].includes(field)) {
      targetPage = isButtonFlow ? 2 : 1;
    } else if (
      [
        'receiverCurrency',
        'amountValue',
        'merchantAmount1',
        'merchantAmount2',
        'merchantAmount3',
        'programNames',
      ].includes(field)
    ) {
      targetPage = isButtonFlow ? 3 : 2;
    } else if (
      ['noteLabel', 'return', 'cancelReturn', 'extraVariablesView'].includes(
        field
      )
    ) {
      targetPage = isButtonFlow ? 4 : 3;
    }

    setCurrentStep(targetPage);
    history.push(getPageUrl(targetPage, currentPath));
  };

  const handleServerSideError = (error) => {
    if (error) {
      setErrorCode(error.name);
      switch (error.name) {
        case 'VALIDATION': {
          // detail[0].field is in the format of button_variable.business
          // get the actual field name
          const errorFieldDetail = _get(error, 'details[0].field');
          const errField = errorFieldDetail
            ? errorFieldDetail.split('.').slice(-1)[0]
            : errorFieldDetail;
          const errMsg = getErrorMsg(errField);
          const clientFieldName = mapServerFieldToClientField(errField);

          const errObj = {};
          errObj[clientFieldName] = errMsg;

          setShouldShowActionableErrorModal(true);
          setErrorDesc(JSON.stringify(errorFieldDetail));
          jumpToErrorPage(clientFieldName);
          return errObj;
        }
        case 'COUNTRY_NOT_ALLOWED':
        case 'UNAVAILABLE_FOR_NON_PPCC': {
          window.location.href = `/donate/buttons/error?name=${ERRORS.COUNTRY_NOT_SUPPORTED}`;
        }
        // eslint-disable-next-line no-fallthrough
        default: {
          setErrorCode('UNACTIONABLE');
          setShouldShowUnactionableErrorModal(true);
          return;
        }
      }
    } else {
      setShouldShowUnactionableErrorModal(true);
    }
  };

  const recordConsentFailure = () => {
    setErrorCode('RECORD_CONSENT_FAILED');
    setErrorDesc('Failed to record user consent status');
    setShouldShowUnactionableErrorModal(true);
    hideSpinner();
  };

  const saveButtonErrorHandler = (response, reject) => {
    const errObj = handleServerSideError(_get(response, 'responseJSON.button'));

    if (!_isEmpty(errObj)) {
      reject(new SubmissionError(errObj));
    }
    hideSpinner();
  };

  const redirectOverviewUrl = (riskStepupStatus, buttonId, contextId, guid) => {
    let overviewUrl = `/donate/buttons/manage/${buttonId}`;
    if (riskStepupStatus === 'PENDING') {
      trackEventWithData(FPTI_SPEC.DONATE_BUTTON_STEPUP_REDIRECT, {
        button_id: buttonId,
        context_id: contextId,
      });
      const flowId = buttonId === 'unhosted' ? 'unhosted' : 'hosted';
      let returnUri = encodeURIComponent(
        `/donate/buttons/poststepup/${buttonId}`
      );
      if (guid) {
        returnUri = encodeURIComponent(
          `/donate/buttons/poststepup/${buttonId}?guid=${guid}`
        );
      }
      window.location.href = `/authflow/entry/?returnUri=${returnUri}&flowId=${flowId}&flowContext=evalcreatedonatebutton&stepupContext=${contextId}`;
      return;
    } else if (riskStepupStatus) {
      const riskStatus =
        riskStepupStatus !== 'ALLOW' ? riskStepupStatus : 'SUCCESS';
      overviewUrl = overviewUrl + `?status=${riskStatus}`;
    }
    history.push(overviewUrl);
  };

  const handleSaveButtonResponse = (response, buttonData) => {
    const riskStepupStatus = response?.data?.riskEligibility?.status;
    const contextId = response?.data?.riskEligibility?.contextId;
    const guid = response?.data?.riskEligibility?.guid;
    const buttonId =
      response?.button?.id ||
      buttonData?.id ||
      response?.data?.riskEligibility?.id;
    if (!_isEmpty(buttonId)) {
      setCurrentStep(1);
      const coverFees = buttonData?.coverFeesEnabled === 'yes';
      trackWizardDetailsPage({
        button_id: buttonId,
        hosted_button_id: buttonId,
        fee_payer: coverFees ? 'variable' : 'merchant',
      });
    } else {
      const receiverCountry = _get(
        response,
        'data.clientData.userData.legalCountry'
      );
      trackWizardDetailsPage({
        rcvrcntry: receiverCountry,
      });
      setCurrentStep(1);
    }
    redirectOverviewUrl(
      riskStepupStatus,
      buttonId || 'unhosted',
      contextId,
      guid
    );
    // destroy form
    // We use `destroyOnUnmount: false` which causes redux-form to save values
    // after unmount. So, manually destroy the form to clear values
    destroyForm();
  };

  const saveButtonHandler = (buttonData, reject) => {
    if (!isDraft && buttonData.buttonName !== buttonName) {
      trackEventWithData(FPTI_SPEC.DONATE_SETUP_WIZARD_PROJECT_NAME_UPDATED); // NOSONAR
    }

    saveButton(buttonData)
      .then((response) => {
        if (!response.ok) {
          saveButtonErrorHandler(response, reject);
        } else {
          handleSaveButtonResponse(response, buttonData);
        }
      })
      .catch((error) => {
        saveButtonErrorHandler(error, reject);
      });
  };

  const handleWizardFormSubmit = (buttonData) => {
    trackEventWithGlobalData(finishBtnFptiSpec, {
      ...(isSetAmountPage
        ? {
            amount_type: amountType,
            choose_program: listPrograms === 'yes' ? 'on' : 'off',
            curr: receiverCurrency,
            fee_type: coverFeesEnabled === 'yes' ? 'offset' : 'none',
            is_recurring: recurringEnabled === 'yes' ? 'on' : 'off',
            step: currentStep,
          }
        : {}),
      ...(isAdvancedSettingsPage
        ? {
            addr_index: noShipping === 'no' ? 'on' : 'off',
            advanced_variables:
              noAdvancedVariables === 'no' && !_isEmpty(extraVariablesView)
                ? 'yes'
                : 'no',
            is_allow_note: noNote === 'no' ? 'yes' : 'no',
            redirect_to_url:
              noCancelReturn === 'no' &&
              (!_isEmpty(cancelReturn) || !_isEmpty(returnUrl))
                ? 'yes'
                : 'no',
            step: currentStep,
          }
        : {}),
      status: buttonStatus,
      action: isDraft ? ACTION.PUBLISH_PAGE : ACTION.FINISH_PAGE,
    });

    // Clear errors on submit
    showSpinner();

    if (isDuplicating) {
      delete buttonData.id;
    } else if (isEditing) {
      buttonData.id = buttonFactoryV3.button?.id;
    }

    // Making sure the submitted button does not have any draft identifiers
    delete buttonData.draftVersion;
    delete buttonData.draftStep;

    return new Promise((resolve, reject) => {
      if (application?.enableUserConsent) {
        recordConsent(application.userConsentData)
          .then((res) => {
            if (res) {
              saveButtonHandler(buttonData, reject);
            } else {
              recordConsentFailure();
            }
          })
          .catch((err) => {
            reject(err);
          });
      } else {
        saveButtonHandler(buttonData, reject);
      }
    });
  };

  const handleImageUpload = async () => {
    trackEventWithData(FPTI_SPEC.UPLOAD_BUTTON_IMAGE_SAVE_CL);
    return new Promise((resolve, reject) => {
      uploadImage(buttonImageUrl)
        .then((response) => {
          if (response.ok) {
            return resolve(response);
          }

          const error = {
            processingStage: 'IMAGE_UPLOAD_ERROR',
            ...response,
          };
          trackEventWithData(FPTI_SPEC.FIELD_LEVEL_ERROR_IM, {
            error_code: 'IMAGE_UPLOAD_ERROR',
            error_desc: 'button_image_url',
            error_detail: JSON.stringify(response),
            screen: SCREEN.EXPERIENCE_SETUP,
            step: currentStep,
          });
          return reject(error);
        })
        .catch((error) => {
          trackEventWithData(FPTI_SPEC.FIELD_LEVEL_ERROR_IM, {
            error_code: 'IMAGE_UPLOAD_ERROR',
            error_desc: 'button_image_url',
            error_detail: JSON.stringify(error),
            screen: SCREEN.EXPERIENCE_SETUP,
            step: currentStep,
          });
          return reject({ ...error, processingStage: 'IMAGE_UPLOAD_ERROR' });
        });
    });
  };

  const handleDropzoneSubmitError = () => {
    setDropzoneError(
      new MessageFormat(worldReady, {
        id: 'buttonsV3.fieldErrors.dropzone.tryAgain',
      }).format()
    );
  };

  const handleDropzoneSubmitSuccess = (isContinue, showError = false) => {
    if (!isContinue) {
      handleSaveDraftClick();
      return;
    }

    if (showError) {
      handleDropzoneSubmitError();
      return;
    }

    handleContinueClick();
  };

  // Image upload Experience
  async function buttonDropzoneSubmit(isContinue = true) {
    const handleUpload = async () => {
      try {
        if (status !== 'D') {
          setShowButtonSpinner(true);
        } else {
          dispatch(draftBusy());
        }
        const response = await handleImageUpload();
        if (response.success) {
          dispatch(change(BTN_FACTORY_V3_FORM, 'buttonImageUrl', response.src));
          formValues.buttonImageUrl = response.src;
          setImageUpdated(false);

          handleDropzoneSubmitSuccess(isContinue);
        } else {
          if (status === 'D') {
            dispatch(uploadImageFail());
          }

          handleDropzoneSubmitError();
        }
        setShowButtonSpinner(false);
      } catch (err) {
        if (isCsamHit(err)) {
          window.location.href = `/donate/buttons/error?name=${ERRORS.RESTRICTED_ACCOUNT}`;
        } else {
          setShowButtonSpinner(false);
          handleDropzoneSubmitError();
        }
      }
    };

    setDropzoneError('');
    if (usePaypalButtonStyle === 'no') {
      if (!_isEmpty(buttonImageUrl)) {
        const isPayPalImage = shouldUsePayPalButtonStyle({
          button_image_url: buttonImageUrl,
        });

        if (isPayPalImage === 'yes') {
          handleDropzoneSubmitError();
        } else if (imageUpdated && isPayPalImage === 'no') {
          handleUpload();
        } else if (_isEmpty(buttonImageUrl)) {
          handleDropzoneSubmitError();
        } else {
          handleDropzoneSubmitSuccess(isContinue);
        }
      } else {
        handleDropzoneSubmitSuccess(isContinue, true);
      }
    } else {
      handleDropzoneSubmitSuccess(isContinue);
    }
  }

  const handleFrequencyErrorCheck = () => {
    if (formValues.recurringEnabled === 'yes') {
      const frequenciesLength = formValues?.frequencies?.length || 0;
      if (frequenciesLength > 0 && frequenciesLength < 3) {
        return true;
      }
      return false;
    }
    return true;
  };

  const handleBannerAndLogoErrorCheck = () => {
    const logoError =
      noLogoImage === 'no' &&
      ((formValues.logoTarget === LOGO_TARGET_BUTTON &&
        _isEmpty(formValues.logoUrl)) ||
        (formValues.logoTarget === LOGO_TARGET_PROFILE &&
          _isEmpty(formValues.businessProfileLogoUrl)) ||
        formValues.logoTarget === LOGO_TARGET_NOLOGO);
    const bannerError = noBannerImage === 'no' && _isEmpty(bannerImageUrl);
    setLogoClientError('');
    setBannerClientError('');
    if (logoError || bannerError) {
      if (logoError) {
        setLogoClientError(
          new MessageFormat(worldReady, {
            id: 'buttonsV3.fieldErrors.logo.missingLogo',
          }).format()
        );
      }
      if (bannerError) {
        setBannerClientError(
          new MessageFormat(worldReady, {
            id: 'buttonsV3.fieldErrors.image.missingImage',
          }).format()
        );
      }
      return false;
    }

    return true;
  };

  const handleContinueFinishButton = (event, isContinue = true) => {
    if (!isDraft) {
      extendSessionAPI({ currentStep: currentStep });
    }

    if (currentStep === 1) {
      if (isDonateLink()) {
        if (!isContinue) {
          handleSaveDraftClick(event);
          return;
        }
        const res = handleBannerAndLogoErrorCheck();
        if (res) {
          handleContinueClick();
        }
      } else {
        buttonDropzoneSubmit(isContinue);
      }
    } else if (currentStep === 2 && isHostedFlow()) {
      let shouldContinue = handleBannerAndLogoErrorCheck();

      if (isDonateLink()) {
        shouldContinue = shouldContinue && handleFrequencyErrorCheck();
      }

      if (shouldContinue) {
        handleContinueClick();
      }
    } else if (currentStep === 3 && isHostedFlow()) {
      if (isDonateButton()) {
        if (handleFrequencyErrorCheck()) {
          handleContinueClick();
        }
      } else {
        handleContinueClick();
      }
    } else {
      handleContinueClick();
    }
  };

  const renderWizardErrorBanner = () => {
    if (!formSubmitStatus) {
      return null;
    }

    return (
      <Alert
        type="error"
        theme="v2"
        className={`wizard-alert ${
          stickToTop && isMobile ? 'sticky-alert' : ''
        }`}
      >
        {new MessageFormat(worldReady, {
          id: 'setup.shared.error.restrictedCharacter.header',
        }).format()}
      </Alert>
    );
  };

  const saveDraftButtonHandler = (
    buttonData,
    isDraftContinue,
    reject,
    resolve
  ) => {
    setShowDraftError(false);
    saveButton(buttonData)
      .then((response) => {
        if (!response.ok) {
          if (buttonData.status === 'D') {
            setShowDraftError(true);
            scrollToWizardsTop(isMobile);
            reject();
          } else {
            saveButtonErrorHandler(response, reject);
          }
        } else {
          if (currentStep === 1 && buttonData.buttonName !== buttonName) {
            trackEventWithData(
              FPTI_SPEC.DONATE_SETUP_WIZARD_PROJECT_NAME_UPDATED
            );
          }
          const buttonId = _get(response, 'button.id');
          if (!_isEmpty(buttonId)) {
            dispatch(change(BTN_FACTORY_V3_FORM, 'id', buttonId));

            const date = _get(response, 'button.epoch_time_updated');

            dispatch(change(BTN_FACTORY_V3_FORM, 'timeUpdated', date));

            const updatedTime = getFormattedTimeOnlyFromUnix(
              date,
              getDonateLocaleAPIFormat()
            );

            setDraftInfo(
              new MessageFormat(worldReady, {
                id: 'buttonsV3.common.lastUpdate',
              }).format({ updatedTime: updatedTime })
            );
          }

          if (isDraftContinue) {
            goToNextStep();
            scrollToWizardsTop(isMobile);
            if (!location.pathname.match('edit')) {
              history.replace(`buttons/${buttonId}/edit`);
            }
          } else if (isDraft && !location.pathname.match('edit')) {
            history.replace(`buttons/${buttonId}/edit`);
          }
          resolve(response);
        }
      })
      .catch(() => {
        if (buttonData.status === 'D') {
          setShowDraftError(true);
          scrollToWizardsTop(isMobile);
          reject();
        } else {
          saveButtonErrorHandler(null, reject);
        }
      });
  };

  function handleSaveDraft(event, isDraftContinue = false) {
    if (isEditing) {
      formValues.id = buttonFactoryV3.button?.id;
    }

    // Setting current step and draft meta for the draft
    formValues.step = currentStep;
    formValues.draftVersion = String(parseInt(formValues.draftVersion, 10) + 1);
    formValues.draftStep = isDraftContinue
      ? String(currentStep + 1)
      : String(currentStep);

    return new Promise((resolve, reject) => {
      saveDraftButtonHandler(formValues, isDraftContinue, reject, resolve);
    });
  }

  const handleButtonImageUrl = () => {
    if (currentStep === 1 && formValues.usePaypalButtonStyle === 'no') {
      const isPayPalImage = shouldUsePayPalButtonStyle({
        button_image_url: formValues.buttonImageUrl,
      });
      if (isPayPalImage === 'yes' || _isEmpty(formValues.buttonImageUrl)) {
        handleDropzoneSubmitError();
        return false;
      }
    }
    return true;
  };

  function handleSaveDraftClick(event) {
    trackEventWithGlobalData(saveBtnFptiSpec, {
      hosted_button_id: _get(match, 'params.buttonId', null),
      step: currentStep,
      status: buttonStatus,
    });
    if (
      !(
        (currentStep === 1 && !handleButtonImageUrl()) ||
        (currentStep === 2 && !handleBannerAndLogoErrorCheck()) ||
        (currentStep === 3 && !handleFrequencyErrorCheck())
      )
    ) {
      handleSaveDraft(event, false);
    }
  }

  const handleQuitDraftSave = (formStateValues) => {
    Object.assign(formValues, formStateValues);

    if (
      !(
        (currentStep === 1 && !handleButtonImageUrl()) ||
        (currentStep === 2 && !handleBannerAndLogoErrorCheck()) ||
        (currentStep === 3 && !handleFrequencyErrorCheck())
      )
    ) {
      handleSaveDraft()
        .then(() => {
          showSpinner();
          window.location.href = '/donate/buttons/manage';
        })
        .catch(() => {
          setShowDraftError(false);
          setShowDraftCreationErrorModal(true);
        });
    }
  };

  const handleDraft = (persist) => {
    setShowQuitModal(false);
    if (persist) {
      if (handleQuitForm) {
        handleQuitForm();
      }
    } else if (id) {
      showSpinner();
      dispatch(deleteButton(id)).then((response) => {
        if (response.ok) {
          window.location.href = '/donate/buttons/manage?deleted=true';
        } else {
          window.location.href = '/donate/buttons/manage?error=DRAFT_ERROR';
        }
      });
    } else {
      window.location.href = '/donate/buttons/manage';
    }
  };

  const renderWizardStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <WizardStep1Form
            setHandleQuitForm={setHandleQuitForm}
            handleQuitDraftSave={handleQuitDraftSave}
            uploadImage={uploadImage}
            handleBack={handleCancelClick}
            onSubmit={handleContinueFinishButton}
            updateBusinessProfileLogo={updateBusinessProfileLogo}
            updateBusinessImageUrls={updateBusinessImageUrls}
            setImageUpdated={setImageUpdated}
            setDropzoneError={setDropzoneError}
            dropzoneError={dropzoneError}
            logoClientError={logoClientError}
            bannerClientError={bannerClientError}
            showSpinner={showButtonSpinner}
            handleSaveDraft={(event) =>
              handleContinueFinishButton(event, false)
            }
            draftInfo={draftInfo}
            isDraft={isDraft}
            showDraftError={showDraftError}
          />
        );

      case 2:
        return (
          <WizardStep2Form
            setHandleQuitForm={setHandleQuitForm}
            handleQuitDraftSave={handleQuitDraftSave}
            uploadImage={uploadImage}
            updateBusinessProfileLogo={updateBusinessProfileLogo}
            updateBusinessImageUrls={updateBusinessImageUrls}
            handleBack={handleBackClick}
            logoClientError={logoClientError}
            bannerClientError={bannerClientError}
            onSubmit={
              isUnhostedFlow() && !isDonateButton()
                ? handleWizardFormSubmit
                : handleContinueFinishButton
            }
            handleSaveDraft={handleSaveDraftClick}
            draftInfo={draftInfo}
            isDraft={isDraft}
            showDraftError={showDraftError}
          />
        );

      case 3:
        return (
          <WizardStep3Form
            setHandleQuitForm={setHandleQuitForm}
            handleQuitDraftSave={handleQuitDraftSave}
            handleBack={handleBackClick}
            onSubmit={
              isHosted && isButtonFlow
                ? handleContinueFinishButton
                : handleWizardFormSubmit
            }
            isDraft={isDraft}
            handleSaveDraft={handleSaveDraftClick}
            draftInfo={draftInfo}
            showDraftError={showDraftError}
          />
        );

      case 4:
        return (
          <WizardStep4Form
            setHandleQuitForm={setHandleQuitForm}
            handleQuitDraftSave={handleQuitDraftSave}
            handleBack={handleBackClick}
            onSubmit={handleWizardFormSubmit}
            isDraft={isDraft}
            handleSaveDraft={handleSaveDraftClick}
            draftInfo={draftInfo}
            showDraftError={showDraftError}
          />
        );

      default:
        return null;
    }
  };

  const renderPreview = () => {
    if (currentStep === 1 && isDonateButton()) {
      return (
        <WizardStep1Preview
          usePaypalButtonStyle={usePaypalButtonStyle === 'yes'}
          buttonSize={buttonSize}
          showAcceptedCardLogos={!!showAcceptedCardLogos}
          buttonImageUrl={buttonImageUrl}
          onError={handleButtonPreviewError}
          locale={getLanguage()}
          country={countryCode}
        />
      );
    }

    return (
      <PreviewNavigator
        className="preview-navigator"
        currentStep={currentStep}
        formValues={formValues}
        fptiSpec={previewTabFptiSpec}
        isMobile={isMobile}
      />
    );
  };

  const stepsString = new MessageFormat(worldReady, {
    id: 'buttonsV3.progressMeter.stepCount',
  }).format({ currentStep, totalSteps });

  if (!isLoggedInUser()) {
    return (
      <ButtonFactoryLogin
        handleLogin={handleLogin}
        handleSignUp={handleSignUp}
      />
    );
  }

  const previewHeader = isButtonConfigurePage
    ? new MessageFormat(worldReady, {
        id: 'buttonsV3.buttonPreview.previewContainerHeader',
      }).format()
    : new MessageFormat(worldReady, {
        id: 'buttonsV3.common.previewTitle',
      }).format();

  const previewButtonLabel = isButtonConfigurePage
    ? new MessageFormat(worldReady, {
        id: 'buttonsV3.common.previewButton',
      }).format()
    : new MessageFormat(worldReady, {
        id: 'buttonsV3.common.previewDonatePage',
      }).format();

  return (
    <>
      {shouldShowDuplicateToast && <DuplicateToast />}
      <Container
        className="button-factory-wizard-v3"
        id="button-factory-wizard-v3"
      >
        <ExtendSession currentStep={currentStep} />
        <Row className="topLinks">
          {!isMobile ? (
            <>
              <div className="goBack">
                {isHostedFlow() && (
                  <Button
                    className="button-link"
                    type="button"
                    onClick={handleTopCancelClick}
                    iconComponent={ArrowLeftIcon}
                    tertiary
                  >
                    {new MessageFormat(worldReady, {
                      id: 'buttonsV3.common.backFundraisers',
                    }).format()}
                  </Button>
                )}
              </div>
              <DonateButtonFeedback
                currentStep={currentStep}
                fptiSpec={feedbackBtnFptiSpec}
                invoker="BUTTON_LINK"
              />
            </>
          ) : (
            <>
              <IconButton
                buttonType="tertiary"
                svgIconComponent={ArrowLeftIcon}
                onClick={
                  currentStep === 1 ? handleTopCancelClick : handleBackClick
                }
              ></IconButton>
              <DonateButtonFeedback
                currentStep={currentStep}
                fptiSpec={feedbackBtnFptiSpec}
                invoker="BUTTON_LINK"
              />
            </>
          )}
        </Row>

        <Row className="button-factory-wizard-v3-content">
          <Col
            className={`col-wizard-step${
              getUserType() === 'PERSONAL' ? ' personal' : ''
            }`}
            lg={4}
          >
            <ProgressMeter
              className="progress-meter-bf3"
              max={totalSteps}
              value={currentStep}
              steps={stepsString}
              title={progressMeterTitles[currentStep]}
            />
            {renderWizardErrorBanner()}
            {!busy && renderWizardStep()}
          </Col>

          {!isMobile ? (
            <Col
              className={`col-button-preview${
                getUserType() === 'PERSONAL' ? ' personal' : ''
              }`}
              lg={8}
            >
              {renderPreview()}
            </Col>
          ) : null}
        </Row>
        {isMobile ? (
          <WizardMobilePreview
            handleSubmit={() => dispatch(submit(BTN_FACTORY_V3_FORM))}
            openMobilePreview={openMobilePreview}
            isMobilePreviewOpen={buttonFactoryV3.isMobilePreviewOpen}
            closeMobilePreview={closeMobilePreview}
            steps={{ currentStep, totalSteps }}
            labels={{
              continueButton: new MessageFormat(worldReady, {
                id: 'buttonsV3.common.continueButton',
              }).format(),
              finishButton: new MessageFormat(worldReady, {
                id: 'buttonsV3.common.finishButton',
              }).format(),
              previewButton: previewButtonLabel,
              previewTitle: previewHeader,
            }}
            usePaypalButtonStyle={usePaypalButtonStyle === 'yes'}
            buttonSize={buttonSize}
            showAcceptedCardLogos={!!showAcceptedCardLogos}
            buttonImageUrl={buttonImageUrl}
            onError={handleButtonPreviewError}
            locale={getLanguage()}
            country={countryCode}
            showSpinner={showButtonSpinner}
            buttonDropzoneSubmit={buttonDropzoneSubmit}
            handleBannerAndLogoErrorCheck={handleBannerAndLogoErrorCheck}
            frequencies={formValues.frequencies}
            recurringEnabled={formValues.recurringEnabled}
          />
        ) : null}
      </Container>

      <ServerValidationErrorModal
        errorCode={errorCode}
        errorDesc={errorDesc}
        isOpen={shouldShowActionableErrorModal}
        setModalState={setShouldShowActionableErrorModal}
        setErrorCode={setErrorCode}
        setErrorDesc={setErrorDesc}
        step={currentStep}
      />

      <DraftAbandonmentErrorModal
        isOpen={showDraftCreationErrorModal}
        setModalState={setShowDraftCreationErrorModal}
        step={currentStep}
        handleTryAgain={() => {
          setShowDraftCreationErrorModal(false);
          handleDraft(true);
        }}
        status={buttonStatus}
      />

      <GenericCreationErrorModal
        errorCode={errorCode}
        formName={BTN_FACTORY_V3_FORM}
        isOpen={shouldShowUnactionableErrorModal}
        screen={screen}
        setModalState={setShouldShowUnactionableErrorModal}
        setErrorCode={setErrorCode}
        step={currentStep}
        hideLink={errorCode === 'RECORD_CONSENT_FAILED'}
        tryAgainClick={handleWizardFormSubmit}
      />

      <QuitModal
        isOpen={showQuitModal}
        setModalState={setShowQuitModal}
        handleDraft={handleDraft}
        isDraft={isDraft}
        step={currentStep}
        status={buttonStatus}
      />

      <GenericDataLoadFailureModal
        errorCode={errorCode}
        errorDesc={errorDesc}
        isOpen={shouldShowGetConsentError}
        setModalState={setShouldShowGetConsentError}
        setErrorCode={setErrorCode}
        setErrorDesc={setErrorDesc}
        step={currentStep}
      />
    </>
  );
};

ButtonFactoryWizardV3.propTypes = {
  application: PropTypes.object,
  buttonFactoryV3: PropTypes.object,
  destroyForm: PropTypes.func,
  dispatch: PropTypes.func,
  form: PropTypes.object,
  getButton: PropTypes.func,
  goToNextStep: PropTypes.func,
  goToPreviousStep: PropTypes.func,
  hideActionableErroModal: PropTypes.func,
  hideUnactionableErrorModal: PropTypes.func,
  history: PropTypes.object,
  initializeWizard: PropTypes.func,
  location: PropTypes.object,
  match: PropTypes.object,
  saveButton: PropTypes.func,
  uploadImage: PropTypes.func,
  updateBusinessProfileLogo: PropTypes.func,
  updateBusinessImageUrls: PropTypes.func,
  updateFlowSpec: PropTypes.func,
  setCurrentStep: PropTypes.func,
  showActionableErrorModal: PropTypes.func,
  showUnactionableErrorModal: PropTypes.func,
  openMobilePreview: PropTypes.func,
  closeMobilePreview: PropTypes.func,
  optionsForLanguage: PropTypes.array,
  draftBusy: PropTypes.func,
  uploadImageFail: PropTypes.func,
  isDraftButton: PropTypes.func,
};

const mapStateToProps = (state) => {
  const { application = {}, buttonFactoryV3 = {}, form = {} } = state;
  const formValues = form.buttonFactoryV3?.values;
  const countryAndLanguageDetails = _get(
    state,
    'application.countryAndLanguageDetails'
  );
  const countryCode =
    _get(formValues, 'countryCode') ||
    _get(state.buttonFactory, 'formattedButton.countryCode');
  return {
    application,
    buttonFactoryV3,
    form,
    optionsForLanguage: getOptionsForLanguageField(
      countryAndLanguageDetails,
      countryCode
    ),
  };
};

const mapDispatchToProps = (dispatch) => {
  return { ...bindActionCreators(ButtonFactoryActions, dispatch), dispatch };
};

const ButtonFactoryWizardFormV3 = connect(
  mapStateToProps,
  mapDispatchToProps
)(withTheme(ButtonFactoryWizardV3));
export default withRouter(ButtonFactoryWizardFormV3);
